import { useSuspenseQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  getMetrics,
  getMetricsFlatData,
  getDataKeys,
  getMaxPeriod,
  type DataPoint,
  GetMetricsParams,
} from '../api'

export type MonthKPIData = {
  kpis: Array<{
    metric_id: number
    key: string
    name: string
    short_name: string
    current_month: number | null
    previous_month: number | null
    ytd: number | null
  }>
  current_period: string
  previous_period: string
}

export function useMonthKPIs(
  competition_id?: number,
  skip_percent_kpis = false,
  overrideParams: Partial<GetMetricsParams> = {}
) {
  return useSuspenseQuery({
    queryKey: [
      'month-kpis',
      competition_id,
      overrideParams.subject_type,
      overrideParams.subject_ids,
    ],
    queryFn: async (): Promise<MonthKPIData> => {
      // Fetch all necessary data in parallel
      // Use subject_type from overrideParams to get the correct max period for the queried subject
      const [metrics, dataKeys, maxPeriodData] = await Promise.all([
        getMetrics(competition_id),
        getDataKeys(),
        getMaxPeriod({
          period_type: 'month',
          subject_type: overrideParams.subject_type ?? 'overall',
          subject_id: overrideParams.subject_ids,
          competition_id,
        }),
      ])

      // Filter for KPIs only
      const kpis = metrics.filter(
        (m) => m.is_kpi && (!skip_percent_kpis || !m.is_percent_metric)
      )

      // Use max period from data, or fall back to current month if no data exists
      const currentPeriod = maxPeriodData.max_period
        ? dayjs(maxPeriodData.max_period).format('YYYY-MM-DD')
        : dayjs().startOf('month').format('YYYY-MM-DD')

      const previousPeriod = dayjs(currentPeriod)
        .subtract(1, 'month')
        .format('YYYY-MM-DD')

      if (kpis.length === 0) {
        return {
          kpis: [],
          current_period: currentPeriod,
          previous_period: previousPeriod,
        }
      }

      // Get component and statistic IDs
      const percentComponentId = dataKeys['percent']
      const valueStatisticId = dataKeys['value']
      const valueYtdStatisticId = dataKeys['value_ytd']

      if (!percentComponentId || !valueStatisticId) {
        throw new Error('Required data keys not found')
      }

      // Prepare query parameters - components and statistics must match metrics count
      const metricIds = kpis.map((kpi) => kpi.metric_id)
      const components = metricIds.map(() => percentComponentId.toString())
      const statistics = metricIds.map(() => valueStatisticId.toString())

      const params = {
        period_type: 'month',
        periods: `${currentPeriod},${previousPeriod}`,
        period_range: false,
        subject_type: 'overall',
        subject_ids: '0',
        metrics: metricIds.join(','),
        components: components.join(','),
        statistics: statistics.join(','),
        competition_id,
        ...overrideParams,
      }

      // Fetch monthly data and YTD data in parallel
      const ytdParams = valueYtdStatisticId
        ? {
            ...params,
            periods: currentPeriod,
            statistics: metricIds
              .map(() => valueYtdStatisticId.toString())
              .join(','),
          }
        : null

      const [data, ytdData] = await Promise.all([
        getMetricsFlatData(params),
        ytdParams ? getMetricsFlatData(ytdParams) : Promise.resolve([]),
      ])

      // Create a map for easy lookup
      const dataMap = new Map<string, number>()
      data.forEach((point: DataPoint) => {
        const key = `${point.period}_${point.metric}`
        dataMap.set(key, point.value)
      })

      // Create YTD map
      const ytdMap = new Map<number, number>()
      ytdData.forEach((point: DataPoint) => {
        ytdMap.set(point.metric, point.value)
      })

      // Build the result
      const result: MonthKPIData = {
        kpis: kpis.map((kpi) => ({
          metric_id: kpi.metric_id,
          key: kpi.key,
          name: kpi.name,
          short_name: kpi.short_name,
          current_month:
            dataMap.get(`${currentPeriod}_${kpi.metric_id}`) ?? null,
          previous_month:
            dataMap.get(`${previousPeriod}_${kpi.metric_id}`) ?? null,
          ytd: ytdMap.get(kpi.metric_id) ?? null,
        })),
        current_period: currentPeriod,
        previous_period: previousPeriod,
      }

      return result
    },
    staleTime: 60 * 1000, // Consider data stale after 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  })
}
